/**
 * Pending receipts (PHASE_pending-receipts-warning.md, Copeland 2026-10-30, with
 * the 2026-10-31 amendment): the open purchase and transfer lines BC still expects
 * to receive at this location, for the items on this count.
 *
 * **Open DOCUMENTS, never stock.** No on-hand appears here, so D-1 is unchanged;
 * the figures are the documents' own outstanding quantities. **A warning, never a
 * block** — it sits beside entry and above the post button and gates nothing.
 *
 * A source BC would not answer is said plainly ("couldn't check purchase
 * orders"); it is never rendered as "none". Nothing here is written to drafts.
 */
import type { PendingReceiptLine, PendingReceipts } from "../api/types";

/** Amendment C's operating rule, verbatim. */
export const PENDING_INSTRUCTION =
  "These items have receipts BC hasn't posted yet. Receive them in BC before you count. " +
  "Count first, then post the count before receiving anything new.";

/** "Open receipt: 6 BAG on PO1001" — the entry row's marker, from the first document. */
export function pendingMarker(lines: PendingReceiptLine[], itemNo: string): string | null {
  const upper = itemNo.trim().toUpperCase();
  const mine = lines.filter((l) => l.item_no.trim().toUpperCase() === upper);
  if (mine.length === 0) return null;
  const first = mine[0]!;
  const text = `Open receipt: ${first.outstanding_qty} ${first.unit_of_measure_code ?? ""} on ${first.document_no}`
    .replace(/\s+/g, " ")
    .trim();
  return mine.length > 1 ? `${text} (+${mine.length - 1} more)` : text;
}

export interface PendingPanelProps {
  data: PendingReceipts | undefined;
  loading: boolean;
  error: string | null;
  /** Item numbers that carry a counted line — highlighted on the review screen. */
  countedItems?: Set<string>;
}

export function PendingReceiptsPanel({ data, loading, error, countedItems }: PendingPanelProps) {
  if (loading) return null;
  if (error || !data || !data.purchase_orders || !data.transfers || !Array.isArray(data.lines)) {
    return (
      <div className="card notice small" data-testid="pending-error">
        <p>
          <strong>Couldn't check pending receipts.</strong> {error ?? "No answer from Business Central."}{" "}
          This is a warning only; counting and posting are not blocked.
        </p>
      </div>
    );
  }

  const unreadable = [data.purchase_orders, data.transfers].filter((s) => s.status === "unreadable");
  if (data.lines.length === 0 && unreadable.length === 0) return null;

  return (
    <details className="card notice small pending" open data-testid="pending-panel">
      <summary>
        <strong>Pending receipts</strong>{" "}
        <span data-testid="pending-count">({data.lines.length})</span>
      </summary>
      {data.lines.length > 0 && <p data-testid="pending-instruction">{PENDING_INSTRUCTION}</p>}
      {unreadable.map((s) => (
        <p key={s.message ?? s.status} className="muted" data-testid="pending-unreadable">
          {s.message ?? "couldn't check"}
        </p>
      ))}
      {data.lines.length > 0 && (
        <ul className="pending__list">
          {data.lines.map((line) => {
            const counted = countedItems?.has(line.item_no.trim().toUpperCase()) === true;
            return (
              <li
                key={`${line.source}|${line.document_no}|${line.item_no}`}
                className={counted ? "pending__line pending__line--counted" : "pending__line"}
                data-testid={`pending-line-${line.document_no}-${line.item_no}`}
              >
                <strong>{line.document_no}</strong>{" "}
                {line.source === "transfer_order" ? "from" : "—"} {line.counterparty ?? "—"} ·{" "}
                {line.item_no} · {line.outstanding_qty} {line.unit_of_measure_code ?? ""}
                {line.in_transit_qty && line.in_transit_qty !== "0" && (
                  <> · in transit {line.in_transit_qty}</>
                )}
                {line.expected_receipt_date && <> · expected {line.expected_receipt_date}</>}
                {counted && <> · counted</>}
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
}
